import {defineMessages, FormattedMessage} from 'react-intl';
import {useCallback, useEffect, useState} from 'react';

import {Button, Icon, Spinner, view} from '@sail/ui';
import {cancelCircle} from '@sail/icons/react/Icon';

import {Conversation} from '@stripe-internal/conversations-client';
import {
  Theme,
  TopBar,
  useConversationUIFeatureFlags,
  useTheme,
} from '@stripe-internal/conversations-ui';

import {
  ConversationScreenshareButtonWithRequestAction,
  type ScreenshareRequestAction,
} from './ConversationScreenshareButton';
import {ConversationSpeakerButton} from './ConversationSpeakerButton';
import {isMobileEntryPoint} from '../../../core/lib/util/conversationUtils';
import {useApp, useEntryPoint} from '../../../core/hooks/appHooks';

const messages = defineMessages({
  chattingWithName: {
    id: 'conversations_assistant.liveChatTopBar.chattingWithName',
    description:
      'Text indicating name of support agent the user is chatting with',
    defaultMessage: 'Chatting with {name}',
  },
  connectingWithAgent: {
    id: 'conversations_assistant.liveChatTopBar.chattingWithGeneric',
    description: 'Text indicating who the user is chatting stripe support',
    defaultMessage: 'Connecting with agent',
  },
  endChatButton: {
    id: 'conversations_assistant.liveChatTopBar.endChatButton',
    description: 'Button to end conversation with an agent',
    defaultMessage: 'End chat',
  },
});

function agentParticipantNames(conversation: Conversation) {
  const participants = Object.values(conversation.participants);

  const names = participants
    .filter((p) => p.role === 'agent')
    .map((p) => p.displayName)
    .filter((name) => !!name);

  return names;
}

type Props = {
  conversation: Conversation;
};

type BaseTopBarProps = Props & {
  onClose: () => void;
  agentNames: string[];
  showSpeakerButton: boolean;
};

type TopBarWithScreenshareProps = BaseTopBarProps & {
  requestAction: ScreenshareRequestAction;
};

type ControlsProps = Props & {
  onClose: () => void;
  showSpeakerButton: boolean;
};

const getLinkEndChatButtonStyles = (theme: Theme) => ({
  backgroundColor: theme.secondaryButtonBackground ?? theme.surfaceHoverColor,
  border: 'none',
  borderRadius: 'medium',
  color: theme.primaryActionColor,
  font: 'body.small' as const,
  fontWeight: theme.secondaryButtonFontWeight,
  keyline: 'none',
  ':hover': {keyline: 'none'},
  ':active': {keyline: 'none'},
});

function Title({agentNames}: {agentNames: string[]}) {
  const theme = useTheme();
  const isLink = theme.audience === 'link';
  let title = <FormattedMessage {...messages.connectingWithAgent} />;

  if (agentNames.length > 0) {
    title = (
      <FormattedMessage
        {...messages.chattingWithName}
        values={{name: agentNames.join(', ')}}
      />
    );
  } else {
    title = (
      <view.div
        css={{stack: 'x', gap: 'xsmall', alignY: 'center'}}
        data-testid="live-chat-connecting-indicator"
      >
        <Spinner size="small" />
        <FormattedMessage {...messages.connectingWithAgent} />
      </view.div>
    );
  }

  return (
    <view.div
      css={{
        font: isLink ? 'label.large.emphasized' : 'label.small.emphasized',
        color: isLink ? theme.textColor : undefined,
      }}
    >
      {title}
    </view.div>
  );
}

function Controls({conversation, onClose, showSpeakerButton}: ControlsProps) {
  const theme = useTheme();
  const isLink = theme.audience === 'link';
  return (
    <view.div css={{stack: 'x', alignX: 'end'}}>
      {showSpeakerButton ? (
        <ConversationSpeakerButton conversation={conversation} />
      ) : null}
      <Button
        size="small"
        onPress={onClose}
        data-testid="conversations-widget-end-chat-button"
        css={isLink ? getLinkEndChatButtonStyles(theme) : undefined}
      >
        <FormattedMessage {...messages.endChatButton} />
      </Button>
    </view.div>
  );
}

function ControlsScreenshare({
  conversation,
  onClose,
  agentNames,
  requestAction,
  showSpeakerButton,
}: TopBarWithScreenshareProps) {
  const theme = useTheme();
  const isLink = theme.audience === 'link';
  return (
    <view.div
      css={{
        stack: 'x',
        gap: 'small',
        width: 'fill',
      }}
    >
      <ConversationScreenshareButtonWithRequestAction
        agentNames={agentNames}
        channel="chat"
        requestAction={requestAction}
      />
      <Button
        size="small"
        onPress={onClose}
        data-testid="conversations-widget-end-chat-button"
        css={{
          flex: 1,
          gap: 'small',
          height: '28px',
          ...(isLink ? getLinkEndChatButtonStyles(theme) : {}),
        }}
      >
        <Icon icon={cancelCircle} />
        <FormattedMessage {...messages.endChatButton} />
      </Button>
      {showSpeakerButton ? (
        <ConversationSpeakerButton
          conversation={conversation}
          screenshareRender
        />
      ) : null}
    </view.div>
  );
}

function TopBarWithoutScreenshare({
  conversation,
  agentNames,
  onClose,
  showSpeakerButton,
}: BaseTopBarProps) {
  return (
    <TopBar data-testId="widget-topbar-live-chat">
      <view.div css={{stack: 'x'}}>
        <Title agentNames={agentNames} />
        <Controls
          conversation={conversation}
          onClose={onClose}
          showSpeakerButton={showSpeakerButton}
        />
      </view.div>
    </TopBar>
  );
}

function TopBarWithScreenshare({
  conversation,
  onClose,
  agentNames,
  requestAction,
  showSpeakerButton,
}: TopBarWithScreenshareProps) {
  return (
    <TopBar data-testId="widget-topbar-live-chat">
      <view.div css={{stack: 'y', gap: 'small'}}>
        <Title agentNames={agentNames} />
        <view.div css={{stack: 'x'}}>
          <ControlsScreenshare
            conversation={conversation}
            onClose={onClose}
            agentNames={agentNames}
            requestAction={requestAction}
            showSpeakerButton={showSpeakerButton}
          />
        </view.div>
      </view.div>
    </TopBar>
  );
}

export function LiveChatTopBarContent({
  conversation,
  onClose,
  requestAction,
  entryPoint,
}: Props & {
  onClose: () => void;
  requestAction: ScreenshareRequestAction;
  entryPoint: string;
}) {
  const {enableScreenshareUi} = useConversationUIFeatureFlags();
  const showSpeakerButton = !isMobileEntryPoint(entryPoint);

  const [agentNames, setAgentNames] = useState<string[]>(
    agentParticipantNames(conversation),
  );

  useEffect(() => {
    const sub = conversation
      .participantsObservable()
      .subscribe(() => setAgentNames(agentParticipantNames(conversation)));

    return () => sub.unsubscribe();
  }, [conversation]);

  if (enableScreenshareUi) {
    return (
      <TopBarWithScreenshare
        conversation={conversation}
        onClose={onClose}
        agentNames={agentNames}
        requestAction={requestAction}
        showSpeakerButton={showSpeakerButton}
      />
    );
  }

  return (
    <TopBarWithoutScreenshare
      conversation={conversation}
      agentNames={agentNames}
      onClose={onClose}
      showSpeakerButton={showSpeakerButton}
    />
  );
}

export function LiveChatTopBar({conversation}: Props) {
  const app = useApp();
  const entryPoint = useEntryPoint();
  const onClose = useCallback(() => {
    app.close();
  }, [app]);
  const requestAction = useCallback<ScreenshareRequestAction>(
    (name, data = {}, waitForResponse = false) => {
      return app.requestAction({
        action: {
          name,
          data,
        },
        waitForResponse,
      });
    },
    [app],
  );

  return (
    <LiveChatTopBarContent
      conversation={conversation}
      onClose={onClose}
      requestAction={requestAction}
      entryPoint={entryPoint}
    />
  );
}
